import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Alert
} from '@mui/material';
import { collection, addDoc, doc, setDoc, getDoc } from 'firebase/firestore';
import { db } from '../../../index';
import { GameSession, GameState, TeamColor, ScreenMode } from '../types';
import { getAnswersForGame } from '../data/answers';
import { TEAM_COLORS } from '../constants/teamColors';
import { generateOpAnswerMappings } from '../data/opAnswerMappings';

export const CreateSession: React.FC = () => {
  const navigate = useNavigate();
  const [sessionName, setSessionName] = useState('');
  const [roomId, setRoomId] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleCreateSession = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!sessionName.trim()) {
      setError('セッション名を入力してください');
      return;
    }

    if (!roomId.trim()) {
      setError('ルームIDを入力してください');
      return;
    }

    // ルームIDの形式チェック（英数字とハイフンのみ、3-20文字）
    if (!/^[a-zA-Z0-9-]{3,20}$/.test(roomId)) {
      setError('ルームIDは3-20文字の英数字とハイフンのみ使用できます');
      return;
    }

    setLoading(true);
    try {
      // ルームIDの重複チェック
      const existingSession = await getDoc(doc(db, 'battleRoyale', roomId));
      if (existingSession.exists()) {
        setError('このルームIDは既に使用されています');
        setLoading(false);
        return;
      }

      // デフォルトのセッションデータを作成
      const sessionData: Omit<GameSession, 'id'> = {
        name: sessionName,
        state: GameState.WAITING,
        answers: getAnswersForGame(),
        teams: [
          { color: TeamColor.RED, displayName: '赤チーム', colorCode: TEAM_COLORS[TeamColor.RED].colorCode, totalScore: 0, scorePercentage: 0 },
          { color: TeamColor.BLUE, displayName: '青チーム', colorCode: TEAM_COLORS[TeamColor.BLUE].colorCode, totalScore: 0, scorePercentage: 0 },
          { color: TeamColor.GREEN, displayName: '緑チーム', colorCode: TEAM_COLORS[TeamColor.GREEN].colorCode, totalScore: 0, scorePercentage: 0 },
          { color: TeamColor.YELLOW, displayName: '黄チーム', colorCode: TEAM_COLORS[TeamColor.YELLOW].colorCode, totalScore: 0, scorePercentage: 0 },
          { color: TeamColor.PURPLE, displayName: '紫チーム', colorCode: TEAM_COLORS[TeamColor.PURPLE].colorCode, totalScore: 0, scorePercentage: 0 },
          { color: TeamColor.ORANGE, displayName: '橙チーム', colorCode: TEAM_COLORS[TeamColor.ORANGE].colorCode, totalScore: 0, scorePercentage: 0 }
        ],
        settings: {
          pointsPerCorrect: 10,
          rateLimit: 10,
          tableCount: 8,
          opAnswerMappings: generateOpAnswerMappings(),
          screenMode: ScreenMode.GROUP_6,
          showTop3Only: false
        }
      };

      // カスタムIDでドキュメントを作成
      await setDoc(doc(db, 'battleRoyale', roomId), sessionData);
      
      // 管理画面へ遷移
      navigate(`/battle-royale/master/${roomId}`);
    } catch (err) {
      console.error('Error creating session:', err);
      setError('セッションの作成に失敗しました');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Container maxWidth="sm">
      <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', py: 4 }}>
        <Paper elevation={3} sx={{ p: 4, width: '100%' }}>
          <Typography variant="h4" component="h1" gutterBottom align="center">
            バトロワなぞとき交渉会
          </Typography>
          
          <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }} align="center">
            新規セッション作成
          </Typography>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          <form onSubmit={handleCreateSession}>
            <TextField
              fullWidth
              label="セッション名"
              value={sessionName}
              onChange={(e) => setSessionName(e.target.value)}
              placeholder="第1回バトロワなぞとき交渉会"
              sx={{ mb: 3 }}
            />

            <TextField
              fullWidth
              label="ルームID"
              value={roomId}
              onChange={(e) => setRoomId(e.target.value.toLowerCase())}
              placeholder="battle-royale-1"
              helperText="英数字とハイフン(-) 3-20文字"
              sx={{ mb: 4 }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={loading || !sessionName.trim() || !roomId.trim()}
              sx={{ py: 2 }}
            >
              セッションを作成
            </Button>
          </form>

          <Box sx={{ mt: 4, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
            <Typography variant="body2" color="text.secondary">
              セッション作成後の流れ：
            </Typography>
            <ol style={{ margin: '8px 0', paddingLeft: '20px' }}>
              <li>正解CSVをインポート</li>
              <li>OPマッピングを設定</li>
              <li>プレイヤー参加用URLを共有</li>
              <li>ゲームを開始</li>
            </ol>
          </Box>
        </Paper>
      </Box>
    </Container>
  );
};